"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Loader2, MapPin, LocateFixed } from '@/components/icons';
import { useToast } from '@/components/ui/use-toast';
import useDebounce from '@/hooks/use-debounce';
import { useSession } from '@/context/SessionContext';
import { getUserAddresses } from '@/lib/supabase/userAddresses';
import { extractCityName } from '@/lib/utils';
import type { NoteLocation } from '@/types/location';

interface LocationAutocompleteProps {
  value: NoteLocation | null;
  onChange: (location: NoteLocation | null) => void;
  onInputChange?: (value: string) => void;
  onFocus?: () => void; // callback when input receives focus
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  forceBlank?: boolean;
  allowAnyPlace?: boolean; // when true, do not filter to city-only results
  showGps?: boolean; // show gps icon to autofill via geolocation
  disableExternalSync?: boolean; // when true, don't sync external value prop to input
  showNotFoundButton?: boolean; // show "No encuentro mi dirección" button at the end of suggestions
  onNotFound?: () => void; // callback when "No encuentro mi dirección" button is clicked
  onCityChange?: (city: string | null) => void; // callback when city search field changes
}

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
  osm_class: string;
  type: string;
  address?: {
    country?: string;
    state?: string;
    city?: string;
    town?: string;
    village?: string;
    borough?: string;
    suburb?: string;
    neighbourhood?: string;
  };
  importance?: number;
}

// Tipos permitidos: SOLO ciudades, pueblos, aldeas y barrios
// Excluye explícitamente provincias, estados y límites administrativos grandes
const ALLOWED_PLACE_TYPES = new Set([
  'city',      // Ciudades principales
  'town',      // Pueblos
  'village',   // Aldeas
  'hamlet',    // Pequeños asentamientos
  'suburb',    // Suburbios
  'neighbourhood', // Barrios
  'borough',   // Distritos urbanos
]);

// Tipos explícitamente descartados
const BLOCKED_TYPES = new Set([
  'country',   // Países
  'state',     // Estados/Provincias
  'county',    // Condados/Provincias
  'province',  // Provincias explícitas
  'region',    // Regiones
]);

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value,
  onChange,
  onInputChange,
  onFocus,
  disabled,
  placeholder = "Escribí una ubicación (ej. Calle Mayor 123)",
  autoFocus = false,
  forceBlank = false,
  allowAnyPlace = false,
  showGps = false,
  disableExternalSync = false,
  showNotFoundButton = false,
  onNotFound,
  onCityChange,
}) => {
  const [inputValue, setInputValue] = useState(value?.displayName || '');
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [loadingGps, setLoadingGps] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [gpsFilled, setGpsFilled] = useState(false);
  const [userLocation, setUserLocation] = useState<{lat: number, lon: number} | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [searchCity, setSearchCity] = useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const selectingRef = React.useRef(false); // Flag para evitar blur cuando se selecciona
  const debouncedInputValue = useDebounce(inputValue, 500);
  const { toast } = useToast();
  const { session, profile, relevantLocation } = useSession();

  const relevantCity = extractCityName(relevantLocation);

  // Sincronizar searchCity: si hay direcciones registradas, obtener ciudad del reverse geocode
  // Si no hay direcciones, usar city por IP
  useEffect(() => {
    const initializeSearchCity = async () => {
      try {
        // Prioridad máxima: mantener la ciudad del navbar (relevantLocation)
        if (relevantCity && searchCity !== relevantCity) {
          setSearchCity(relevantCity);
          return;
        }

        // Si el usuario está logueado, verificar si tiene direcciones
        if (session?.user?.id) {
          const addresses = await getUserAddresses(session.user.id);
          
          if (addresses.length > 0) {
            // Usuario tiene direcciones: hacer reverse geocode de la primera dirección
            const firstAddr = addresses[0];
            if (firstAddr.location?.latitude && firstAddr.location?.longitude) {
              const lat = firstAddr.location.latitude;
              const lon = firstAddr.location.longitude;
              
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`,
                { mode: 'cors' }
              );
              
              if (response.ok) {
                const data = await response.json();
                const addr = data.address || {};
                const cityName = addr.city || addr.town || addr.village || addr.municipality || addr.suburb || addr.neighbourhood;
                
                if (cityName && !searchCity) {
                  setSearchCity(cityName);
                }
              }
            }
          } else if (userCity && !searchCity) {
            // Sin direcciones registradas: usar city por IP
            setSearchCity(userCity);
          }
        } else if (userCity && !searchCity) {
          // Usuario no logueado: usar city por IP
          setSearchCity(userCity);
        }
      } catch (error) {
        console.error('Error initializing search city:', error);
        // Fallback a userCity si algo falla
        if (userCity && !searchCity) {
          setSearchCity(userCity);
        }
      }
    };

    initializeSearchCity();
  }, [session?.user?.id, userCity, searchCity, relevantCity]);

  // Obtener ubicación del usuario por IP - DISABLED: Always use Salta as default
  useEffect(() => {
    // IP geolocation disabled - default location is always Salta
    // Users can manually select a different city if they want
    // Previously: const getUserLocationFromIP = async () => { ... fetch('https://ipapi.co/json/') ... }
  }, [toast]);

  // Notify parent when city search field changes
  useEffect(() => {
    onCityChange?.(searchCity);
  }, [searchCity, onCityChange]);

  // Sincronizar el estado interno del input con el valor externo (ej. si el GPS lo actualiza)
  useEffect(() => {
    if (forceBlank || disableExternalSync) return; // Skip sync while user is editing
    const externalDisplayName = value?.displayName || '';
    if (inputValue !== externalDisplayName) {
      setInputValue(externalDisplayName);
    }
    setSuggestions([]);
  }, [value, forceBlank, disableExternalSync]);

  const truncateBeforeComma = (s: string) => {
    if (!s) return s;
    const idx = s.indexOf(',');
    return idx === -1 ? s : s.slice(0, idx);
  };

  const displayedPlaceholder = truncateBeforeComma(placeholder || '');

  /**
   * Verifica si un resultado es una ciudad/pueblo/aldea válida
   * Descarta explícitamente provincias y límites administrativos grandes
   */
  const isValidCity = (result: NominatimResult): boolean => {
    if (!result) return false;

    // 1. Descartar explícitamente tipos bloqueados
    if (BLOCKED_TYPES.has(result.type)) {
      return false;
    }

    // 2. Si el objeto 'address' incluye una localidad explícita, confiar en eso (mejor precisión)
    const addr = result.address || {};
    if (
      addr.city ||
      addr.town ||
      addr.village ||
      // hamlet is not always present in address, but we still accept if type indicates it
      addr.neighbourhood ||
      addr.suburb ||
      addr.borough
    ) {
      return true;
    }

    // 3. Aceptar resultados de clase 'place' con tipos permitidos
    if (result.osm_class === 'place' && ALLOWED_PLACE_TYPES.has(result.type)) {
      return true;
    }

    // 4. Rechazar límites administrativos grandes (provincias/estados/regiones)
    if (
      result.osm_class === 'boundary' ||
      result.osm_class === 'admin' ||
      result.type === 'administrative' ||
      result.type === 'county'
    ) {
      return false;
    }

    // 5. Por defecto, aceptar sólo si el tipo está en la lista permitida
    return ALLOWED_PLACE_TYPES.has(result.type);
  };

  const fetchSuggestions = useCallback(async (term: string) => {
    if (term.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoadingSuggestions(true);
    try {
      // Construir término de búsqueda: dirección, ciudad, país
      let searchTerm = term;
      if (searchCity && !term.includes(',')) {
        searchTerm = `${term}, ${searchCity}, Argentina`;
      }

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchTerm)}&limit=15&addressdetails=1&countrycodes=ar`,
        { mode: 'cors' }
      );
      if (!response.ok) throw new Error('Failed to fetch location suggestions');
      const data: NominatimResult[] = await response.json();

      // Si `allowAnyPlace` es false, filtrar resultados para aceptar
      // solo ciudades/pueblos/aldeas/barrios (ver `isValidCity`).
      let usable = allowAnyPlace ? data : data.filter((r) => isValidCity(r));
      
      // Filtro suave: solo rechazar resultados claramente incorrectos
      // (ej: "Salta 150" cuando buscamos en Salta)
      if (searchCity && allowAnyPlace) {
        const searchCityLower = searchCity.toLowerCase();
        const searchTermLower = term.toLowerCase();
        
        usable = usable.filter(r => {
          const displayLower = r.display_name.toLowerCase();
          
          // Rechazar si el resultado COMIENZA con "CIUDAD NÚMERO" y el término NO contiene esa ciudad
          // Ej: rechazar "Salta 150" si buscamos "florida 150" en Salta
          if (displayLower.startsWith(`${searchCityLower} `) && /\d+/.test(displayLower.substring(searchCityLower.length + 1))) {
            // Verificar si el término buscado contiene la ciudad
            if (!searchTermLower.includes(searchCityLower)) {
              return false;
            }
          }
          
          return true;
        });
      }
      
      // Reordenar: priorizar direcciones reales (con road + house_number)
      usable.sort((a, b) => {
        const aHasRoad = !!(a.address?.road && a.address?.house_number);
        const bHasRoad = !!(b.address?.road && b.address?.house_number);
        
        if (aHasRoad && !bHasRoad) return -1;
        if (!aHasRoad && bHasRoad) return 1;
        
        // Si ambos tienen road o ambos no, ordenar por importancia
        return (b.importance || 0) - (a.importance || 0);
      });
      
      // Ordenar por distancia al usuario si tenemos su ubicación
      if (userLocation) {
        const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
          const R = 6371; // Radio de la Tierra en km
          const dLat = (lat2 - lat1) * Math.PI / 180;
          const dLon = (lon2 - lon1) * Math.PI / 180;
          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                    Math.sin(dLon/2) * Math.sin(dLon/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          return R * c;
        };
        
        const withDistance = usable.map(r => ({
          ...r,
          distance: calculateDistance(userLocation.lat, userLocation.lon, parseFloat(r.lat), parseFloat(r.lon)),
          matchesUserCity: searchCity ? r.display_name.toLowerCase().includes(searchCity.toLowerCase()) : false
        }));
        
        // Ordenar: primero los que coinciden con la ciudad de búsqueda, luego por distancia
        withDistance.sort((a, b) => {
          if (a.matchesUserCity && !b.matchesUserCity) return -1;
          if (!a.matchesUserCity && b.matchesUserCity) return 1;
          return a.distance - b.distance;
        });
        setSuggestions(withDistance.slice(0, 8));
      } else {
        setSuggestions(usable.slice(0, 8));
      }
    } catch (error) {
      console.error("Error fetching location suggestions:", error);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [userLocation, searchCity, allowAnyPlace]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // When forcing blank, clear inputValue first, otherwise keep existing
      if (forceBlank) setInputValue('');
      // Increased timeout to ensure element is mounted, visible, and keyboard can open
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.click();
        setIsFocused(true);
      }, 100);
    }
  }, [autoFocus, forceBlank]);

  useEffect(() => {
    // Evitar re-búsqueda si estamos en medio de una selección iniciada por el usuario
    if (selectingRef.current) return;

    if (debouncedInputValue && debouncedInputValue !== value?.displayName) {
      fetchSuggestions(debouncedInputValue);
    } else {
      setSuggestions([]);
    }
  }, [debouncedInputValue, fetchSuggestions, value?.displayName]);

  const handleSelectSuggestion = (result: NominatimResult) => {
    // Marcar que estamos seleccionando para prevenir que el blur/disparador de búsqueda
    // reabra el dropdown inmediatamente.
    selectingRef.current = true;

    const addr = result.address || {};
    const cityName = addr.city || addr.town || addr.village || addr.municipality || addr.suburb || addr.neighbourhood;

    const newLocation: NoteLocation = {
      displayName: result.display_name,
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon),
      cityName: cityName,
      road: addr.road || addr.street || addr.pedestrian || addr.cycleway,
      houseNumber: addr.house_number || addr.street_number || addr.addr_street_number,
    };
    setInputValue(result.display_name);
    setSuggestions([]);
    setIsFocused(false);
    setGpsFilled(false);
    
    // Llamar onChange inmediatamente (no en setTimeout)
    onChange(newLocation);
    
    // Liberar el flag después
    setTimeout(() => {
      selectingRef.current = false;
    }, 100);
  };

  const useGps = async () => {
    if (!showGps) return;
    if (!('geolocation' in navigator)) {
      toast({ title: 'GPS no disponible', description: 'Tu navegador no soporta geolocalización.' });
      return;
    }
    setLoadingGps(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const p = pos.coords;
          // reverse geocode with nominatim
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${p.latitude}&lon=${p.longitude}&zoom=18&addressdetails=1`,
            { mode: 'cors' }
          );
          if (!r.ok) throw new Error('reverse failed');
          const j = await r.json();
          // Preferir ciudades: si el resultado no es una ciudad válida, intentar extraer
          // la localidad (city/town/village/suburb/neighbourhood/borough) desde el address.
          const display_raw = j.display_name || '';
          const addr = j.address || {};
          const cityName = addr.city || addr.town || addr.village || addr.municipality || addr.suburb || addr.neighbourhood;
          const road = addr.road || addr.street || addr.pedestrian || addr.cycleway;
          const houseNumber = addr.house_number || addr.street_number || addr.addr_street_number;

          if (allowAnyPlace || isValidCity(j)) {
            setInputValue(display_raw);
            const newLocation: NoteLocation = { 
              displayName: display_raw, 
              latitude: p.latitude, 
              longitude: p.longitude,
              cityName: cityName,
              road,
              houseNumber
            };
            onChange(newLocation);
            setSuggestions([]);
            setGpsFilled(true);
          } else {
            const cityLike = cityName;
            if (cityLike) {
              const country = addr.country || '';
              const display = country ? `${cityLike}, ${country}` : cityLike;
              setInputValue(display);
              const newLocation: NoteLocation = { 
                displayName: display, 
                latitude: p.latitude, 
                longitude: p.longitude,
                cityName: cityName
              };
              onChange(newLocation);
              setSuggestions([]);
                setGpsFilled(true);
            } else {
              toast({ title: 'Ciudad no encontrada', description: 'No se detectó una ciudad cercana con GPS.' });
            }
          }
        } catch (e) {
          console.error('gps reverse error', e);
          toast({ title: 'Error GPS', description: 'No se pudo obtener la dirección cercana.' });
        } finally {
          setLoadingGps(false);
        }
      },
      (err) => {
        console.error('geolocation error', err);
        toast({ title: 'Error GPS', description: 'No fue posible obtener la ubicación.' });
        setLoadingGps(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValue = e.target.value;
    setInputValue(newInputValue);
    setGpsFilled(false);
    onInputChange?.(newInputValue);

    if (!newInputValue.trim()) {
      onChange(null);
    }
  };

  return (
    <div className="relative">
      <div className="space-y-2">
        {/* Dirección + Ciudad en una fila */}
        <div className="flex items-center gap-2">
          {/* Input de dirección */}
          <div className="relative flex-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="location-autocomplete-input"
              placeholder="Escribí una dirección"
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={() => {
                setIsFocused(true);
                if (inputValue.length >= 3) {
                  fetchSuggestions(inputValue);
                }
                onFocus?.();
              }}
              onBlur={() => {
                setTimeout(() => {
                  if (!selectingRef.current) {
                    setIsFocused(false);
                  }
                  selectingRef.current = false;
                }, 100);
              }}
              disabled={disabled}
              className="pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>

          {/* Texto "en" */}
          <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">en</span>

          {/* Input de ciudad (editable) */}
          <Input
            value={searchCity || ''}
            onChange={(e) => setSearchCity(e.target.value)}
            placeholder="Ciudad"
            className="w-32 focus-visible:ring-0 focus-visible:ring-offset-0"
          />

          {/* GPS button */}
          {showGps && (
            <button
              onClick={useGps}
              title="Usar GPS"
              className="p-2 rounded hover:bg-muted flex items-center justify-center"
            >
              {loadingGps ? (
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              ) : (
                <LocateFixed className="h-5 w-5 text-primary" />
              )}
            </button>
          )}
        </div>
      </div>

      {((suggestions.length > 0 || (loadingSuggestions && inputValue.length >= 3)) || (showNotFoundButton && inputValue.length >= 3)) && isFocused && (
        <div className="mt-3 space-y-2">
          {suggestions.length > 0 ? (
            suggestions.map((suggestion, index) => {
              // Formatear dirección al estilo argentino: "Calle Número" en lugar de "Número, Calle"
              const parts = suggestion.display_name.split(',').map(p => p.trim());
              let displayTitle = parts[0];
              let displaySubtitle = suggestion.display_name;
              
              if (parts.length >= 2) {
                const firstPart = parts[0].trim();
                const secondPart = parts[1].trim();
                // Si la primera parte es solo un número, invertir el orden
                if (/^\d+$/.test(firstPart)) {
                  displayTitle = `${secondPart} ${firstPart}`;
                  // Eliminar redundancia: quitar "número, calle," del inicio de la segunda línea
                  const remainingParts = parts.slice(2);
                  displaySubtitle = remainingParts.length > 0 ? remainingParts.join(', ') : suggestion.display_name;
                }
              }
              
              return (
                <button
                  key={index}
                  className="w-full text-left p-3 border rounded-lg hover:bg-muted/50 transition-colors flex flex-col gap-1 bg-background"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    selectingRef.current = true;
                    handleSelectSuggestion(suggestion);
                  }}
                  disabled={disabled}
                >
                  <div className="font-medium text-sm">{displayTitle}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">{displaySubtitle}</div>
                </button>
              );
            })
            ) : (
            loadingSuggestions && (
              <div className="p-3 flex items-center justify-center text-sm text-muted-foreground border rounded-lg">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Buscando...
              </div>
            )
          )}
          
          {/* Botón "No encuentro mi dirección" */}
          {showNotFoundButton && onNotFound && (
            <button
              className="w-full text-left p-3 border rounded-lg hover:bg-muted/50 transition-colors text-sm text-muted-foreground font-medium bg-background flex items-center justify-center"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                selectingRef.current = true;
                onNotFound();
              }}
              disabled={disabled}
            >
              No encuentro mi dirección
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;